// ---- Coded sleeved robe / cloak (custom Three.js geometry) ----
// Target silhouette: Jedi-style outer robe (refs: open front, arms IN sleeves,
// hangs from shoulders to ankles, optional hood). NOT a back cape.
//
// Seating after the prim build:
//   body  → torso limb   (open-front shell, shoulder yoke)
//   sleeves → shoulderL / shoulderR  (tubes along the arm so limbs ride inside)
//   hood  → head limb    (optional)

import * as THREE from "three";
import { TOON_SMOOTH, toonify } from "../toon.ts";
import {
  AVERAGE_MALE_HOOD,
  AVERAGE_MALE_SEAT,
  AVERAGE_MALE_SLEEVE,
  hoodSeatFromRig,
  recordCodedWearSeat,
  sleeveSeatFromRig,
  wearSeatFromRig,
  type HoodSeat,
  type SleeveSeat,
} from "./seat.ts";

// Re-export seating helpers so `from "./cloak.ts"` still works.
export {
  wearSeatFromRig,
  yokeFromRig,
  hoodSeatFromRig,
  sleeveSeatFromRig,
} from "./seat.ts";

export type CloakBuildOpts = {
  hooded: boolean;
  cloth?: number;
  clothDk?: number;
  lining?: number;
};

// Warm travel-robe palette (toon-readable; close to classic Jedi outer robe).
const DEFAULT_CLOTH = 0x6a5746;
const DEFAULT_CLOTH_DK = 0x4e4034;
const DEFAULT_LINING = 0x8a7358;

function cloakMat(color: number): THREE.MeshToonMaterial {
  const m = toonify(
    new THREE.MeshToonMaterial({
      color,
      gradientMap: TOON_SMOOTH,
      side: THREE.DoubleSide,
    }),
  );
  m.userData.owned = true;
  return m;
}

function mesh(geo: THREE.BufferGeometry, color: number): THREE.Mesh {
  const m = new THREE.Mesh(geo, cloakMat(color));
  m.castShadow = true;
  m.receiveShadow = false;
  m.userData.codedWear = true;
  return m;
}

/** Open lathe shell: profile radius(x) vs height(y); gap centered on +Z (front). */
function openShell(
  profile: THREE.Vector2[],
  segs: number,
  gapRad: number,
  color: number,
  /** When true, lower verts follow the hip gait (leg-clearance). */
  hemFollow = false,
): THREE.Mesh {
  const geo = new THREE.LatheGeometry(profile, segs, gapRad / 2, Math.PI * 2 - gapRad);
  geo.computeVertexNormals();
  const m = mesh(geo, color);
  if (hemFollow) m.userData.cloakHem = true;
  return m;
}

/** Full lathe tube (sleeves — arms go through the hole). */
function tubeShell(profile: THREE.Vector2[], segs: number, color: number): THREE.Mesh {
  const geo = new THREE.LatheGeometry(profile, segs);
  geo.computeVertexNormals();
  return mesh(geo, color);
}

// ---- Torso-local robe body -------------------------------------------------
// Body is parented to the `torso` limb (pivot = hip). All y's below are
// TORSO-LOCAL. The shoulder joint / head pivot are measured from the live
// rig at attach time — never hardcode a magic yoke height.
//
// On average male (s=1.1): shelfY ≈ 0.58, headY ≈ 0.62, tunic top ≈ 0.69.
// Ankles ≈ −1.12.

const HEM_Y = -1.12;
/** Open front V on the body shell — chest shows (ref open robe). */
const BODY_GAP = 1.35;
/** Shoulder wrap uses a tighter front gap so deltoids stay covered. */
const SHOULDER_GAP = 0.75;

function bodyProfile(yokeY: number, shelfY: number): THREE.Vector2[] {
  // Monastic traveler robe (ref: thin collar, fabric drapes flat over shoulders
  // into sleeves — NOT a puffy yoke/capelet). Shelf meets sleeve roots without
  // adding bulk at the neck.
  return [
    new THREE.Vector2(0.14, yokeY), // thin neck opening
    new THREE.Vector2(0.22, yokeY - 0.02), // flat on collarbones
    new THREE.Vector2(0.31, shelfY), // modest shoulder — not a pad
    new THREE.Vector2(0.29, shelfY - 0.16), // underarm scoop
    new THREE.Vector2(0.26, 0.15), // upper back
    new THREE.Vector2(0.24, -0.15), // waist pinches in
    new THREE.Vector2(0.27, -0.5), // begins to open
    new THREE.Vector2(0.34, -0.85), // lower flare
    new THREE.Vector2(0.4, HEM_Y), // ankle flare (still modest)
  ];
}

function liningProfile(yokeY: number, shelfY: number): THREE.Vector2[] {
  return bodyProfile(yokeY, shelfY).map((p) => new THREE.Vector2(Math.max(0.04, p.x - 0.015), p.y));
}

function collarProfile(yokeY: number, _shelfY: number, hooded = false): THREE.Vector2[] {
  // Neck-only ring — short + narrow so it never reaches the shoulder shelf.
  // Extending out to shelfY was clipping a dark band through the shoulder wrap.
  // Hooded: climb a little higher so the head-local hood joins cleanly.
  if (hooded) {
    return [
      new THREE.Vector2(0.13, yokeY + 0.04),
      new THREE.Vector2(0.17, yokeY + 0.01),
      new THREE.Vector2(0.18, yokeY - 0.03),
    ];
  }
  return [
    new THREE.Vector2(0.13, yokeY + 0.005),
    new THREE.Vector2(0.165, yokeY - 0.015),
    new THREE.Vector2(0.17, yokeY - 0.04),
  ];
}

/**
 * Thin flat shoulder wrap — covers deltoid tops without the thick capelet bulk.
 * Short vertical band + modest radius + tighter front gap than the body shell
 * so fabric drapes over the shoulders into the sleeves (ref silhouette).
 */
function shoulderWrapProfile(yokeY: number, shelfY: number): THREE.Vector2[] {
  // Short vertical band (thin, not capelet-thick) but wide enough to meet sleeves.
  return [
    new THREE.Vector2(0.16, yokeY - 0.01),
    new THREE.Vector2(0.28, shelfY + 0.02),
    new THREE.Vector2(0.34, shelfY - 0.01), // under the sleeve root
    new THREE.Vector2(0.3, shelfY - 0.09),
  ];
}

/** Vertical front hem of the open robe. */
function frontEdge(side: -1 | 1, color: number, yokeY: number): THREE.Mesh {
  const ang = side * (BODY_GAP / 2 + 0.02);
  const shape = new THREE.Shape();
  const w = 0.028;
  shape.moveTo(-w, HEM_Y);
  shape.lineTo(w, HEM_Y);
  shape.lineTo(w * 0.6, yokeY - 0.1);
  shape.lineTo(-w * 0.4, yokeY);
  shape.closePath();
  const geo = new THREE.ExtrudeGeometry(shape, { depth: 0.014, bevelEnabled: false, curveSegments: 3 });
  geo.rotateY(Math.PI / 2);
  geo.translate(0, 0, -0.007);
  const m = mesh(geo, color);
  const r = 0.26;
  m.position.set(Math.sin(ang) * r, 0, Math.cos(ang) * r);
  m.rotation.y = ang;
  return m;
}

function buildBody(
  cloth: number,
  clothDk: number,
  lining: number,
  yokeY: number,
  shelfY: number,
  hooded = false,
): THREE.Group {
  const body = new THREE.Group();
  body.name = "cloak-body";
  body.userData.codedWear = true;

  // Outer + lining follow the hip gait below the waist (legs clear the hem).
  body.add(openShell(liningProfile(yokeY, shelfY), 28, BODY_GAP, lining, true));
  body.add(openShell(bodyProfile(yokeY, shelfY), 32, BODY_GAP, cloth, true));
  // Neck-only collar FIRST (under the wrap) — same face gap as the body so its
  // open edges don't stick out of the shoulder fabric.
  body.add(openShell(collarProfile(yokeY, shelfY, hooded), 16, BODY_GAP, clothDk, false));
  // Thin flat shoulder wrap on top — covers deltoid tops, hides collar outer.
  body.add(openShell(shoulderWrapProfile(yokeY, shelfY), 18, SHOULDER_GAP, cloth, false));
  body.add(frontEdge(-1, clothDk, yokeY));
  body.add(frontEdge(1, clothDk, yokeY));

  // Tiny rear bias so the open V clears the chest — keep hug tight.
  body.position.z = -0.02;
  return body;
}

// ---- Hem follow (walk/run) -------------------------------------------------
// The body shell is parented to the TORSO; hips are siblings that swing the
// legs through it. Each frame we warp lower verts with the hip pitch so the
// hem rides the stride instead of the calves punching through a rigid tube.

/** Torso-local y where hem follow starts (0 = hip pivot). */
const HEM_FOLLOW_Y = 0.05;
/** z displacement scale (world-ish units per rad of hip.rx). */
const HEM_FOLLOW_Z = 0.48;
/** Outward |x| push so the thigh clears the side wall. */
const HEM_FOLLOW_X = 0.14;

type CloakHemState = {
  meshes: { mesh: THREE.Mesh; base: Float32Array }[];
};

function captureHemBases(root: THREE.Object3D): CloakHemState {
  const meshes: CloakHemState["meshes"] = [];
  root.traverse((o) => {
    if (!o.userData?.cloakHem || !(o as THREE.Mesh).isMesh) return;
    const m = o as THREE.Mesh;
    const pos = m.geometry.getAttribute("position");
    // Own a copy — LatheGeometry buffers are unique per mesh after build.
    meshes.push({ mesh: m, base: Float32Array.from(pos.array as ArrayLike<number>) });
  });
  return { meshes };
}

/**
 * Warp lower cloak verts from hip swing. Call AFTER animateGait each frame.
 * No-ops when no cloak is equipped (userData.cloakHem missing/empty).
 */
export function updateCloakHem(root: THREE.Object3D): void {
  const state = root.userData.cloakHem as CloakHemState | undefined;
  if (!state?.meshes.length) return;

  const hipL = findLimb(root, "hipL");
  const hipR = findLimb(root, "hipR");
  if (!hipL || !hipR) return;

  // Gait writes hip.rotation.x: +rx swings that foot toward +Z (character front).
  const swingL = hipL.rotation.x;
  const swingR = hipR.rotation.x;
  const range = HEM_FOLLOW_Y - HEM_Y; // positive

  for (const { mesh: m, base } of state.meshes) {
    const pos = m.geometry.getAttribute("position") as THREE.BufferAttribute;
    const arr = pos.array as Float32Array;
    for (let i = 0; i < base.length; i += 3) {
      const bx = base[i]!;
      const by = base[i + 1]!;
      const bz = base[i + 2]!;
      // Weight 0 above waist → 1 at hem (eased).
      let t = (HEM_FOLLOW_Y - by) / range;
      if (t <= 0) {
        arr[i] = bx;
        arr[i + 1] = by;
        arr[i + 2] = bz;
        continue;
      }
      if (t > 1) t = 1;
      t = t * t; // more motion at the hem than the thigh

      // Side pick: left / right / blend on the center seam.
      let swing: number;
      if (bx < -0.04) swing = swingL;
      else if (bx > 0.04) swing = swingR;
      else swing = (swingL + swingR) * 0.5;

      // Follow the leg fore/aft; push out a little so the calf clears the wall.
      const out = Math.sign(bx || 1) * Math.abs(swing) * HEM_FOLLOW_X * t;
      arr[i] = bx + out;
      arr[i + 1] = by;
      arr[i + 2] = bz + swing * HEM_FOLLOW_Z * t;
    }
    pos.needsUpdate = true;
    // Skip computeVertexNormals — too hot for 60fps; toon still reads fine.
  }
}

// ---- Shoulder-local sleeves ------------------------------------------------
// At rest the upper arm hangs along −Y in shoulder space. Radii + cuff y come
// from SleeveSeat (live arm mesh) — male deltoids are thicker than female;
// freehand r=0.078 pokes through male arms.
//
// Silhouette rules:
//  1. Deltoid pad MUST close at r=0 on top — open lathes = hollow chimney tubes.
//  2. Pad stays FLAT/low (ref: fabric drapes over shoulder, not a puffy spaulder).
//  3. Outer sleeve starts under the pad and hangs — continuous with the body shelf.

function buildSleeve(
  cloth: number,
  clothDk: number,
  lining: number,
  seat: SleeveSeat,
): THREE.Group {
  const g = new THREE.Group();
  g.name = "cloak-sleeve";
  g.userData.codedWear = true;
  g.userData.sleeveR = seat.sleeveR;

  const { sleeveR, padR, elbowY, cuffY, deltoidTopY } = seat;
  // Closed flat pad over the deltoid — tall enough to hide shirt arms, low
  // enough to stay cloth (not spaulder bulk). Flow straight into the tube.
  const capTop = Math.max(deltoidTopY * 1.25, 0.12);
  const capR = padR * 1.08;
  const cap = [
    new THREE.Vector2(0, capTop), // CLOSED — no hollow chimney top
    new THREE.Vector2(capR * 0.55, capTop * 0.75),
    new THREE.Vector2(capR * 0.95, capTop * 0.3),
    new THREE.Vector2(capR, 0.0),
    new THREE.Vector2(sleeveR * 1.02, -0.05), // flow into the sleeve tube
  ];
  g.add(tubeShell(cap, 12, cloth));

  // Outer sleeve: clear measured armR; slight flare at the hand.
  const outer = [
    new THREE.Vector2(sleeveR * 1.02, -0.03),
    new THREE.Vector2(sleeveR * 1.02, elbowY * 0.2),
    new THREE.Vector2(sleeveR * 1.04, elbowY),
    new THREE.Vector2(sleeveR * 1.08, (elbowY + cuffY) * 0.5),
    new THREE.Vector2(sleeveR * 1.12, cuffY),
  ];
  g.add(tubeShell(outer, 14, cloth));

  // Inner lining — cuff flash.
  const inner = outer.map((p) => new THREE.Vector2(Math.max(seat.armR * 0.45, p.x - 0.014), p.y));
  g.add(tubeShell(inner, 10, lining));

  // Dark cuff band.
  const cuff = [
    new THREE.Vector2(sleeveR * 1.05, cuffY + 0.04),
    new THREE.Vector2(sleeveR * 1.16, cuffY + 0.015),
    new THREE.Vector2(sleeveR * 1.1, cuffY - 0.02),
  ];
  g.add(tubeShell(cuff, 10, clothDk));

  return g;
}

// ---- Head-local hood -------------------------------------------------------
// Traveler robe hood (ref: continuous with the cloak, closed crown over brow,
// open face). Join + apex from HoodSeat — NEVER freehand.
// Head-local: y=0 at the neck pivot, +z = face, skull top ≈ 0.47 (s=1.1).
//
// Join rule: body collar lives in torso-local; shelf ≈ neckY−0.04, yoke ≈
// neckY+0.06 → head-local join band ≈ −0.04…+0.06. Capelet MUST end there —
// not hang to −0.2 (that reads as a separate hanging shell).

/** Head-local y where the capelet meets the robe collar (shelf of the body). */
function hoodJoinY(seat: HoodSeat): number {
  // At/just above the neck pivot — overlaps standing collar.
  return 0.02 * (seat.scale / 1.1);
}

/** Radius at the join — match body collar outer (~0.30 on avg male). */
function hoodJoinR(seat: HoodSeat): number {
  return Math.max(seat.headR * 1.65, seat.shellR * 1.35, 0.29);
}

/**
 * Lower cowl (OPEN face): join → ears → brow line.
 * Face gap only lives here so eyes/mouth stay open.
 */
function hoodCowlProfile(seat: HoodSeat): THREE.Vector2[] {
  const { shellR, earY, napeY, hairlineY, headR } = seat;
  const joinY = hoodJoinY(seat);
  const joinR = hoodJoinR(seat);
  return [
    new THREE.Vector2(joinR, joinY), // meets robe collar
    new THREE.Vector2(joinR * 0.95, joinY + 0.04), // short neck rise
    new THREE.Vector2(shellR * 1.05, napeY), // nape
    new THREE.Vector2(shellR * 1.1, earY), // over ears
    new THREE.Vector2(shellR * 1.08, hairlineY - 0.02), // cheek / lower brow (face hole ends)
    new THREE.Vector2(headR * 1.05, hairlineY + 0.02), // tuck under closed crown
  ];
}

/**
 * Closed crown (NO face gap): brow → apex.
 * Front of the top is solid fabric — traveler hood peak over the forehead.
 */
function hoodCrownProfile(seat: HoodSeat): THREE.Vector2[] {
  const { shellR, hairlineY, crownY, apexY, headR } = seat;
  return [
    new THREE.Vector2(headR * 1.08, hairlineY - 0.02),
    new THREE.Vector2(shellR * 1.08, hairlineY + 0.04),
    new THREE.Vector2(shellR * 0.95, crownY),
    new THREE.Vector2(shellR * 0.48, apexY - 0.02),
    new THREE.Vector2(0, apexY),
  ];
}

function buildHood(cloth: number, clothDk: number, seat: HoodSeat): THREE.Group {
  const g = new THREE.Group();
  g.name = "cloak-hood";
  g.userData.codedWear = true;
  g.userData.hoodApexY = seat.apexY;
  g.userData.hoodShellR = seat.shellR;

  // --- Lower cowl: open face, joins the robe at the collar band ---
  const cowl = hoodCowlProfile(seat);
  const faceGap = 1.2; // eyes/nose/mouth; crown above is closed separately
  g.add(openShell(cowl, 26, faceGap, cloth));
  g.add(
    openShell(
      cowl.map((p) => new THREE.Vector2(Math.max(0.04, p.x - 0.012), p.y)),
      16,
      faceGap + 0.06,
      clothDk,
    ),
  );

  // --- Closed crown: full lathe — front of the top is CLOSED over the brow ---
  const crown = hoodCrownProfile(seat);
  g.add(tubeShell(crown, 24, cloth)); // no face gap
  g.add(
    tubeShell(
      crown.map((p) => new THREE.Vector2(Math.max(0.03, p.x - 0.012), p.y)),
      16,
      clothDk,
    ),
  );
  // Slight forward bias so the closed peak sits over the forehead, not the occiput.
  // (Apply to crown meshes only — last two children.)
  const n = g.children.length;
  for (let i = n - 2; i < n; i++) {
    g.children[i]!.position.z = seat.headR * 0.1;
  }

  return g;
}

// ---- Public API ------------------------------------------------------------

export function findLimb(root: THREE.Object3D, name: string): THREE.Object3D | null {
  let found: THREE.Object3D | null = null;
  root.traverse((o) => {
    if (o.userData?.limb === name) found = o;
  });
  return found;
}

/** Parent robe body, sleeves, and optional hood onto a built humanoid rig. */
export function attachCloak(root: THREE.Group, opts: CloakBuildOpts): void {
  const cloth = opts.cloth ?? DEFAULT_CLOTH;
  const clothDk = opts.clothDk ?? DEFAULT_CLOTH_DK;
  const lining = opts.lining ?? DEFAULT_LINING;

  const torso = findLimb(root, "torso");
  if (!torso) return;

  // Seat the yoke from the live shoulder + head joints (scale/sex/build safe).
  // NEVER hardcode YOKE_Y — see src/wear/seat.ts + wearable-authoring.
  const seat = wearSeatFromRig(root);
  const { shelfY, yokeY } = seat;
  torso.add(buildBody(cloth, clothDk, lining, yokeY, shelfY, opts.hooded));

  // Sleeves from live arm mesh (male thicker than female) — never freehand r.
  const sleeveSeat = sleeveSeatFromRig(root);
  const sleeveL = buildSleeve(cloth, clothDk, lining, sleeveSeat);
  const sleeveR = buildSleeve(cloth, clothDk, lining, sleeveSeat);
  // Slight inboard so the pad nests under the shoulder wrap edge.
  // shoulderL = viewer's right (−x). No y-drop — pad sits at the joint.
  sleeveL.position.x = 0.02;
  sleeveR.position.x = -0.02;
  const shL = findLimb(root, "shoulderL");
  const shR = findLimb(root, "shoulderR");
  if (shL) shL.add(sleeveL);
  else torso.add(sleeveL);
  if (shR) shR.add(sleeveR);
  else torso.add(sleeveR);

  let hoodRec: { seat: HoodSeat; apexY: number; shellR: number } | undefined;
  if (opts.hooded) {
    // Hood from crown-frame / scalp math (head-local) — not freehand apex 0.42.
    const hoodSeat = hoodSeatFromRig(root);
    const hood = buildHood(cloth, clothDk, hoodSeat);
    const head = findLimb(root, "head");
    if (head) head.add(hood);
    else torso.add(hood);
    hoodRec = { seat: hoodSeat, apexY: hoodSeat.apexY, shellR: hoodSeat.shellR };
  }
  recordCodedWearSeat(root, opts.hooded ? "hooded-cloak" : "cloak", yokeY, {
    hood: hoodRec,
    sleeve: { seat: sleeveSeat, sleeveR: sleeveSeat.sleeveR },
  });

  // Capture rest positions for the gait-driven hem warp (updateCloakHem).
  // Merge with any existing entry if multiple coded wear ever stack.
  const captured = captureHemBases(root);
  const prev = root.userData.cloakHem as CloakHemState | undefined;
  root.userData.cloakHem = prev
    ? { meshes: [...prev.meshes, ...captured.meshes] }
    : captured;
}

/** Freestanding ground / shop drop (open robe + stub sleeves). */
export function buildCloakGround(group: THREE.Group, opts: CloakBuildOpts): void {
  const cloth = opts.cloth ?? DEFAULT_CLOTH;
  const clothDk = opts.clothDk ?? DEFAULT_CLOTH_DK;
  const lining = opts.lining ?? DEFAULT_LINING;

  const h0 = 0.05;
  const h1 = 1.4;
  const pts = [
    new THREE.Vector2(0.12, h1),
    new THREE.Vector2(0.18, h1 - 0.12),
    new THREE.Vector2(0.22, h1 * 0.5),
    new THREE.Vector2(0.24, h0),
  ];
  const gap = 1.4;
  group.add(openShell(pts, 20, gap, cloth));
  group.add(
    openShell(
      pts.map((p) => new THREE.Vector2(Math.max(0.04, p.x - 0.02), p.y)),
      16,
      gap,
      lining,
    ),
  );
  group.add(
    openShell(
      [
        new THREE.Vector2(0.12, h1 + 0.03),
        new THREE.Vector2(0.22, h1),
        new THREE.Vector2(0.2, h1 - 0.1),
      ],
      14,
      0.8,
      clothDk,
    ),
  );

  // Stub sleeves stuck out at the shoulders (display pose).
  for (const sx of [-1, 1] as const) {
    const sleeve = buildSleeve(cloth, clothDk, lining, AVERAGE_MALE_SLEEVE);
    sleeve.position.set(sx * 0.28, h1 - 0.15, 0);
    sleeve.rotation.z = sx * 0.35; // slightly out
    sleeve.scale.setScalar(0.85);
    group.add(sleeve);
  }

  if (opts.hooded) {
    const hood = buildHood(cloth, clothDk, AVERAGE_MALE_HOOD);
    hood.position.set(0, h1 - 0.12, 0);
    hood.scale.setScalar(0.95);
    group.add(hood);
  }
}

/** @deprecated pieces API — use attachCloak. Kept for any external callers. */
export function buildCloakPieces(opts: CloakBuildOpts): {
  body: THREE.Group;
  hood: THREE.Group | null;
} {
  const cloth = opts.cloth ?? DEFAULT_CLOTH;
  const clothDk = opts.clothDk ?? DEFAULT_CLOTH_DK;
  const lining = opts.lining ?? DEFAULT_LINING;
  // No live rig — fall back to average-male torso/head-local numbers.
  const { shelfY, yokeY } = AVERAGE_MALE_SEAT;
  return {
    body: buildBody(cloth, clothDk, lining, yokeY, shelfY, opts.hooded),
    hood: opts.hooded ? buildHood(cloth, clothDk, AVERAGE_MALE_HOOD) : null,
  };
}
